import { useQuery, onlineManager } from '@tanstack/react-query';
import { useMemo } from 'react';
import { DashboardService } from '../../service/dashboard/DashboardService';
import type {
  AnalyticsDateRange,
  DataSource,
  SubscriptionAnalyticsOverview,
  SubscriptionRevenue,
  UserAnalyticsOverview,
} from '../../types/api/dashboard';

const REFETCH_INTERVAL_MS = 30_000;
const STALE_TIME_MS = 15_000;

interface AnalyticsQueryResult<T> {
  data?: T;
  dataSource: DataSource;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  dataUpdatedAt: number;
}

/** Common React Query options for every analytics hook. */
const sharedOptions = {
  refetchInterval: REFETCH_INTERVAL_MS,
  staleTime: STALE_TIME_MS,
  refetchOnWindowFocus: true,
  refetchOnReconnect: 'always' as const,
};

function useService(): DashboardService {
  return useMemo(() => new DashboardService(), []);
}

/**
 * Hook: GET /admin/analytics/users/overview
 * Auto-refreshes every 30s; falls back to a small mock fixture on failure.
 */
export function useUserAnalyticsOverview(
  range: AnalyticsDateRange,
): AnalyticsQueryResult<UserAnalyticsOverview> {
  const service = useService();
  const query = useQuery({
    queryKey: ['analytics', 'users', 'overview', range.from, range.to] as const,
    queryFn: () => service.getUserAnalyticsOverview(range),
    enabled: onlineManager.isOnline(),
    ...sharedOptions,
  });
  return mapResult(query);
}

/**
 * Hook: GET /admin/analytics/subscriptions/overview
 */
export function useSubscriptionAnalyticsOverview(
  range: AnalyticsDateRange,
): AnalyticsQueryResult<SubscriptionAnalyticsOverview> {
  const service = useService();
  const query = useQuery({
    queryKey: ['analytics', 'subscriptions', 'overview', range.from, range.to] as const,
    queryFn: () => service.getSubscriptionAnalyticsOverview(range),
    enabled: onlineManager.isOnline(),
    ...sharedOptions,
  });
  return mapResult(query);
}

/**
 * Hook: GET /admin/analytics/subscriptions/revenue
 */
export function useSubscriptionRevenue(
  range: AnalyticsDateRange,
): AnalyticsQueryResult<SubscriptionRevenue> {
  const service = useService();
  const query = useQuery({
    queryKey: ['analytics', 'subscriptions', 'revenue', range.from, range.to] as const,
    queryFn: () => service.getSubscriptionRevenue(range),
    enabled: onlineManager.isOnline(),
    ...sharedOptions,
  });
  return mapResult(query);
}

// ────────────────────────── Internal ──────────────────────────────

interface RawQueryShape<T> {
  data?: { data: T; source: DataSource };
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  dataUpdatedAt: number;
}

function mapResult<T>(query: RawQueryShape<T>): AnalyticsQueryResult<T> {
  return {
    data: query.data?.data,
    dataSource: query.data?.source ?? 'mock',
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    dataUpdatedAt: query.dataUpdatedAt,
  };
}
